import React from 'react';
import { FormattedMessage } from 'react-intl';

import { DefaultIntlFormatValues } from '@ratehub/base-ui';

import MESSAGES from '../definitions/Messages';
import CreditCardShape from '../definitions/CreditCardShape';
import { PRODUCT_TYPES } from '../definitions/CurrentDocument';
import FooterDefaultIntlFormatValues from '../definitions/FooterDefaultIntlFormatValues';
import getSummarizedEarnings from './getSummarizedEarnings';
import isEarningsAPercentage from './isEarningsAPercentage';
import isCIBCCreditCard from './isCIBCCreditCard';


/**
 * Get rowTitle and rowSummary pairs for the CreditCardLayout Focus Details section, with optional rowDetails
 * @param {CreditCardShape} cardInfo - the card
 * @param {string} productType - the card’s product type
 * @param {import('react-intl').IntlShape} intl - internationalization object
 * @returns {null|Array<{
 *              rowTitle: React.Element,
 *              rowSummary: React.Element,
 *              rowDetails: {key: string, message: React.Element},
 *              key: string,
 *         }>} - title, summary, and maybe details for the focus details area of a credit card
 */
function getCardLayoutFocusDetailsRows(cardInfo, productType, intl) {
    switch (productType) {
        case PRODUCT_TYPES.LOW_INTEREST:
            return getLowInterestDetails({ cardInfo });

        case PRODUCT_TYPES.BALANCE_TRANSFER:
            return getBalanceTransferDetails({ cardInfo });

        case PRODUCT_TYPES.TRAVEL:
        case PRODUCT_TYPES.CASH_BACK:
        case PRODUCT_TYPES.ANY_REWARDS:
        case PRODUCT_TYPES.STORE_CREDIT:
            return getRewardDetails({ cardInfo, productType, intl });

        case PRODUCT_TYPES.SECURED:
            return getSecuredDetails({ cardInfo });

        default:
            // not just 'unsupported', it would be an illegal product type
            return null;
    }
}

function getLowInterestDetails({ cardInfo }) {
    const {
        annualFee,
        isFirstYearFeeWaived,
        lowInterestMonthlyBalance,
        lowInterestMonthlyInterestFee,
    } = cardInfo;
    const isCIBC = isCIBCCreditCard(cardInfo);

    return [
        {
            key: 'pay per month',
            rowTitle: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.low-interest.pay-per-month.title"
                    defaultMessage="Pay {lowInterestMonthlyInterestFee, number, CAD0}/mo"
                    values={{ lowInterestMonthlyInterestFee }}
                />
            ),
            rowSummary: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.low-interest.pay-per-month.summary"
                    defaultMessage="Based on a credit balance of {lowInterestMonthlyBalance, number, CAD0}"
                    values={{ lowInterestMonthlyBalance }}
                />
            ),
        },

        getAnnualFeeFocusDetailsRow({ annualFee, isFirstYearFeeWaived, isCIBC }),
    ];
}

function getBalanceTransferDetails({ cardInfo }) {
    const {
        annualFee,
        isFirstYearFeeWaived,
        balanceTransferMonthlyPayment,
        balanceTransferPromoMonths,
        balanceTransferAmount,
        balanceTransferFeeRate,
    } = cardInfo;
    const isCIBC = isCIBCCreditCard(cardInfo);

    return [
        {
            key: 'pay per month',
            rowTitle: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.balance-transfer.pay-per-month.title"
                    defaultMessage="Pay {balanceTransferMonthlyPayment, number, CAD2}/mo"
                    values={{ balanceTransferMonthlyPayment }}
                />
            ),
            rowSummary: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.balance-transfer.pay-per-month.summary"
                    defaultMessage={`during the promo to pay off
                        {balanceTransferAmount, number, CAD0} transferred balance`}
                    values={{
                        balanceTransferPromoMonths,
                        balanceTransferAmount,
                    }}
                />
            ),
        },
        {
            key: 'balance transfer fee',
            rowTitle: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.balance-transfer.balance-transfer-fee.title"
                    defaultMessage="{balanceTransferFeeRate, number, percent} Balance transfer fee"
                    values={{ balanceTransferFeeRate: balanceTransferFeeRate / 100 }}
                />
            ),
            rowSummary: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.balance-transfer.balance-transfer-fee.summary"
                    defaultMessage="A fee of {dollarValue, number, CAD0} will be added to your balance"
                    values={{ dollarValue: (balanceTransferFeeRate / 100) * balanceTransferAmount }}
                />
            ),
        },

        getAnnualFeeFocusDetailsRow({ annualFee, isFirstYearFeeWaived, isCIBC }),
    ];
}

function getRewardDetails({ cardInfo, productType, intl }) {
    const {
        id: cardId,
        productType: cardProductType,
        annualFee,
        isFirstYearFeeWaived,
        earningsDescription,
        welcomeBonusDescription,
        maxSignUpBonusDollars,
        maxSignUpBonusPoints,
        anniversaryBonusDescription,
        anniversaryBonus,
    } = cardInfo;
    const isCIBC = isCIBCCreditCard(cardInfo);

    const isEarningsShownAsPercentage = isEarningsAPercentage({ productType, cardId, cardProductType });
    const earningsSummary = getSummarizedEarnings(isEarningsShownAsPercentage, earningsDescription, intl);

    // always start with earning rewards
    const rows = [
        {
            key: 'earn rewards',
            rowTitle: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.earn-rewards.title"
                    defaultMessage="Earn rewards"
                />
            ),
            rowSummary: earningsSummary.summaryMessage,
            rowDetails: earningsSummary.summaryDetails,
        },
    ];

    // add Welcome bonus info if we have sufficient data
    if (welcomeBonusDescription && maxSignUpBonusDollars && (maxSignUpBonusPoints || isEarningsShownAsPercentage)) {
        rows.push({
            key: 'welcome bonus',
            rowTitle: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.welcome-bonus.title"
                    defaultMessage="Welcome bonus"
                />
            ),
            // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
            rowSummary: isEarningsShownAsPercentage
                ? (
                    <FormattedMessage
                        id="edb.credit-card.focus-details.welcome-bonus.dollars"
                        defaultMessage="Earn up to {maxSignUpBonusDollars, number, CAD0}"
                        values={{
                            maxSignUpBonusDollars,
                        }}
                    />
                )
                : (
                    <FormattedMessage
                        id="edb.credit-card.focus-details.welcome-bonus.points"
                        defaultMessage="Earn up to {maxSignUpBonusPoints, number} points (a {maxSignUpBonusDollars, number, CAD0} value)"
                        values={{
                            maxSignUpBonusPoints,
                            maxSignUpBonusDollars,
                        }}
                    />
                ),
            rowDetails: [ {
                key: welcomeBonusDescription,
                message: welcomeBonusDescription,
            } ],
        });
    }

    // add Anniversary bonus info if we have sufficient data
    if (
        anniversaryBonusDescription
        && anniversaryBonus?.maxValue
        && (anniversaryBonus?.maxPoints || isEarningsShownAsPercentage)
    ) {
        rows.push({
            key: 'anniversary bonus',
            rowTitle: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.anniversary-bonus.title"
                    defaultMessage="Anniversary bonus"
                />
            ),
            // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
            rowSummary: isEarningsShownAsPercentage
                ? (
                    <FormattedMessage
                        id="edb.credit-card.anniversary-bonus.dollars"
                        defaultMessage="Earn up to {anniversaryBonusValue, number, CAD0}"
                        values={{
                            ...DefaultIntlFormatValues,
                            anniversaryBonusValue: anniversaryBonus.maxValue,
                        }}
                    />
                )
                : (
                    <FormattedMessage
                        id="edb.credit-card.anniversary-bonus.value"
                        defaultMessage="Earn up to {anniversaryBonusPoints, number} points (a {anniversaryBonusValue, number, CAD0} value)"
                        values={{
                            ...DefaultIntlFormatValues,
                            anniversaryBonusPoints: anniversaryBonus.maxPoints,
                            anniversaryBonusValue: anniversaryBonus.maxValue,
                        }}
                    />
                ),
            rowDetails: [ {
                key: anniversaryBonusDescription,
                message: anniversaryBonusDescription,
            } ],
        });
    }

    rows.push(getAnnualFeeFocusDetailsRow({ annualFee, isFirstYearFeeWaived, isCIBC }));

    return rows;
}

function getSecuredDetails({ cardInfo }) {
    const {
        annualFee,
        isFirstYearFeeWaived,
        securedMonthlyBalance,
        securedMonthlyInterestFee,
    } = cardInfo;
    const isCIBC = isCIBCCreditCard(cardInfo);

    return [
        {
            key: 'monthly interest',
            rowTitle: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.secured.pay-per-month.title"
                    defaultMessage="Pay {securedMonthlyInterestFee, number, CAD2}/mo in interest"
                    values={{ securedMonthlyInterestFee }}
                />
            ),
            rowSummary: (
                <FormattedMessage
                    id="edb.credit-card.focus-details.secured.pay-per-month.summary"
                    defaultMessage="Based on a credit balance of {securedMonthlyBalance, number, CAD0}"
                    values={{ securedMonthlyBalance }}
                />
            ),
        },

        getAnnualFeeFocusDetailsRow({ annualFee, isFirstYearFeeWaived, isCIBC }),
    ];
}


function getAnnualFeeFocusDetailsRow({ annualFee, isFirstYearFeeWaived, isCIBC }) {
    return {
        key: 'annual fee',
        rowTitle: (
            <FormattedMessage
                id="edb.credit-card.annual-fee"
                defaultMessage="Annual fee"
            />
        ),
        // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
        rowSummary: isCIBC
            ? (
                <FormattedMessage
                    id="edb.credit-card.annual-fee.cibc.summary"
                    defaultMessage="see Details"
                />
            )
            : (
                <FormattedMessage
                    {...MESSAGES.annualFeeSummary}
                    values={{
                        annualFee,
                        isFirstYearFeeWaived,
                        zero: 0,
                        ...FooterDefaultIntlFormatValues,
                        ...DefaultIntlFormatValues,
                    }}
                />
            ),
    };
}


export default getCardLayoutFocusDetailsRows;
